import axiosInstance from './axios';
import {
  LeaveRequest,
  LeaveRequestDetail,
  LeaveRequestStatus,
  PaginatedResponse,
  HalfDayType,
} from '../../types';

export interface CreateLeaveRequestDto {
  leaveTypeId: number;
  startDate: string; // ISO date string
  endDate: string;
  halfDayType?: HalfDayType;
  reason: string;
}

export interface UpdateLeaveRequestDto {
  leaveTypeId?: number;
  startDate?: string;
  endDate?: string;
  halfDayType?: HalfDayType;
  reason?: string;
}

export interface ApproveLeaveRequestDto {
  comments?: string;
}

export interface RejectLeaveRequestDto {
  reason: string;
}

export interface OverridePaidStatusDto {
  isPaid: boolean;
  reason?: string;
}

export interface LeaveRequestFilters {
  employeeId?: number;
  status?: LeaveRequestStatus;
  startDate?: string;
  endDate?: string;
  page?: number;
  pageSize?: number;
}

class LeaveRequestsService {
  private basePath = '/leave-requests';

  /**
   * Create a new leave request (draft)
   */
  async create(data: CreateLeaveRequestDto): Promise<LeaveRequestDetail> {
    const response = await axiosInstance.post<LeaveRequestDetail>(
      this.basePath,
      data
    );
    return response.data;
  }

  /**
   * Get all leave requests with filters
   */
  async getAll(
    filters?: LeaveRequestFilters
  ): Promise<PaginatedResponse<LeaveRequestDetail>> {
    const response = await axiosInstance.get<
      PaginatedResponse<LeaveRequestDetail>
    >(this.basePath, { params: filters });
    return response.data;
  }

  /**
   * Get my leave requests
   */
  async getMyRequests(
    status?: LeaveRequestStatus,
    page?: number,
    pageSize?: number
  ): Promise<PaginatedResponse<LeaveRequestDetail>> {
    const response = await axiosInstance.get<
      PaginatedResponse<LeaveRequestDetail>
    >(`${this.basePath}/me`, {
      params: { status, page, pageSize },
    });
    return response.data;
  }

  /**
   * Get pending approvals for current manager
   */
  async getPendingApprovals(): Promise<LeaveRequestDetail[]> {
    const response = await axiosInstance.get<LeaveRequestDetail[]>(
      `${this.basePath}/pending-approvals`
    );
    return response.data;
  }

  /**
   * Get leave request by ID
   */
  async getById(id: number): Promise<LeaveRequestDetail> {
    const response = await axiosInstance.get<LeaveRequestDetail>(
      `${this.basePath}/${id}`
    );
    return response.data;
  }

  /**
   * Update a draft leave request
   */
  async update(
    id: number,
    data: UpdateLeaveRequestDto
  ): Promise<LeaveRequestDetail> {
    const response = await axiosInstance.patch<LeaveRequestDetail>(
      `${this.basePath}/${id}`,
      data
    );
    return response.data;
  }

  /**
   * Submit a leave request for approval
   */
  async submit(id: number): Promise<{
    success: boolean;
    message: string;
    leaveRequest: LeaveRequestDetail;
  }> {
    const response = await axiosInstance.post(
      `${this.basePath}/${id}/submit`
    );
    return response.data;
  }

  /**
   * Approve a leave request
   */
  async approve(
    id: number,
    data?: ApproveLeaveRequestDto
  ): Promise<{
    success: boolean;
    message: string;
    leaveRequest: LeaveRequestDetail;
  }> {
    const response = await axiosInstance.post(
      `${this.basePath}/${id}/approve`,
      data
    );
    return response.data;
  }

  /**
   * Reject a leave request
   */
  async reject(
    id: number,
    data: RejectLeaveRequestDto
  ): Promise<{
    success: boolean;
    message: string;
    leaveRequest: LeaveRequestDetail;
  }> {
    const response = await axiosInstance.post(
      `${this.basePath}/${id}/reject`,
      data
    );
    return response.data;
  }

  /**
   * Cancel a leave request
   */
  async cancel(id: number): Promise<{
    success: boolean;
    message: string;
    leaveRequest: LeaveRequestDetail;
  }> {
    const response = await axiosInstance.post(
      `${this.basePath}/${id}/cancel`
    );
    return response.data;
  }

  /**
   * Override paid/unpaid status (HR only)
   */
  async overridePaidStatus(
    id: number,
    data: OverridePaidStatusDto
  ): Promise<{
    success: boolean;
    message: string;
    leaveRequest: LeaveRequestDetail;
  }> {
    const response = await axiosInstance.post(
      `${this.basePath}/${id}/override-paid`,
      data
    );
    return response.data;
  }
}

export const leaveRequestsService = new LeaveRequestsService();
